import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import { useI18n } from 'builder/hooks/useI18n'
import { actions as uiActions } from 'builder/modules/ui'
import { Icon24, Icon20 } from 'builder/components/Icon'
import Markdown from './Markdown'
import { Icon } from './assets'

import {
  Avatar,
  ChatBotBody,
  ChatBotForm,
  Loader,
  ChatBotContainer,
  ScrollIndicator,
  ErrorMessage,
  LoadingDots,
  Close,
  WelcomeContainer,
  WelcomeContent,
  WelcomeTitle,
  WelcomeSubtitle,
  ChatHeader,
  ChatTitle,
  DateSeparator,
  MessageContainer,
  MessageBubble,
  MessagesWrapper,
  IconWrapper,
  InputContainer,
  InputWrapper,
  SendButton,
  Reactions,
  ReactionButton,
  ApplySuggestionsContainer,
  ApplySuggestions,
  Toast,
  SuggestionsContainer,
  SuggestionButton,
} from './styles'
import { useChatScroll, useConversationalBuilder } from './hooks'
import AutoExpandingPromptInput from './PromptInput'

type Props = {
  className?: string
}

const ConversationalBuilder = ({ className }: Props) => {
  const dispatch = useDispatch()
  const { i18n } = useI18n()

  const suggestions = [
    {
      action: i18n.t('builder.conversational_builder.generate_summary'),
      prompt: i18n.t('builder.conversational_builder.generate_summary_prompt'),
    },
    {
      action: i18n.t('builder.conversational_builder.check_grammar'),
      prompt: i18n.t('builder.conversational_builder.check_grammar_prompt'),
    },
    {
      action: i18n.t('builder.conversational_builder.polish'),
      prompt: i18n.t('builder.conversational_builder.polish_prompt'),
    },
    {
      action: i18n.t('builder.conversational_builder.shorten'),
      prompt: i18n.t('builder.conversational_builder.shorten_prompt'),
    },
  ]
  const {
    messages,
    error,
    loading,
    handleSubmit,
    handleRefresh,
    pendingChanges,
    handleApplyChanges,
    sendMessage,
  } = useConversationalBuilder()
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [inputValue, setInputValue] = useState('')
  const [messageFeedback, setMessageFeedback] = useState<Record<number, 'like' | 'dislike'>>({})
  const [showToast, setShowToast] = useState(false)
  const [applyState, setApplyState] = useState<'idle' | 'applying' | 'applied'>('idle')
  const scrollUtils = useChatScroll({ open: true, messages: messages })
  const { chatBodyRef, trackScroll, showScrollIndicator, scrollToBottom } = scrollUtils

  useEffect(() => {
    if (inputRef.current && !loading) {
      inputRef.current.focus()
    }
  }, [loading])

  useEffect(() => {
    if (inputValue && messages && messages.length > 0) {
      const lastMessage = messages[messages.length - 1]
      if (lastMessage.role === 'user' && lastMessage.content === inputValue.trim()) {
        setInputValue('')
      }
    }
  }, [messages, inputValue])

  useEffect(() => {
    if (pendingChanges.length === 0) {
      setApplyState('idle')
    }
  }, [pendingChanges])

  useEffect(() => {
    if (pendingChanges.length > 0) {
      trackInternalEvent('chat_suggestions_received', { label: 'builder' })
    }
  }, [pendingChanges])

  useEffect(() => {
    if (error) trackInternalEvent('chat_error', { label: 'builder', error })
  }, [error])

  const showWelcomeScreen = !messages || messages.length === 0

  const handleSendClick = () => {
    if (inputRef.current?.form && inputValue.trim()) {
      inputRef.current.form.requestSubmit()
      setInputValue('')
    }
  }

  const handleFeedback = (messageIndex: number, feedback: 'like' | 'dislike') => {
    setMessageFeedback(prev => ({ ...prev, [messageIndex]: feedback }))
    trackInternalEvent('chat_feedback', {
      label: 'builder',
      feedback: feedback,
    })

    setShowToast(true)
    setTimeout(() => setShowToast(false), 3000)
  }

  const handleRefreshWithCleanup = () => {
    const lastMessageIndex = messages.length - 1
    setMessageFeedback(prev => {
      const newFeedback = { ...prev }
      delete newFeedback[lastMessageIndex]
      return newFeedback
    })

    handleRefresh()
  }

  const handleApplyWithState = async () => {
    trackInternalEvent('chat_suggestions_applied', { label: 'builder' })
    setApplyState('applying')
    await handleApplyChanges()
    setApplyState('applied')
  }

  const handleSuggestionClick = (prompt: string, actionName: string) => {
    trackInternalEvent('chat_click_quick_action', {
      label: 'builder',
      action: actionName,
    })
    setInputValue(prompt)
    sendMessage(prompt)
    setInputValue('')
  }

  return (
    <ChatBotContainer className={className}>
      {showToast && <Toast>{i18n.t('builder.conversational_builder.feedback_thanks')}</Toast>}
      <Close
        onClick={() => {
          trackInternalEvent('chat_closed', { label: 'builder' })
          dispatch(uiActions.toggleConversationalBuilder(false))
        }}
      >
        <Icon24.Close />
      </Close>

      {showWelcomeScreen ? (
        <WelcomeContainer>
          <WelcomeContent>
            <Icon.AiCoach width={40} height={40} />
            <WelcomeTitle>{i18n.t('builder.conversational_builder.title')}</WelcomeTitle>
            <WelcomeSubtitle>{i18n.t('builder.conversational_builder.subtitle')}</WelcomeSubtitle>

            <ChatBotForm onSubmit={handleSubmit} $withPadding>
              <InputWrapper style={{ maxWidth: 480, margin: '0 auto' }}>
                <AutoExpandingPromptInput
                  name="prompt"
                  autoComplete="off"
                  autoFocus={true}
                  placeholder={i18n.t('builder.conversational_builder.ask_anything_placeholder')}
                  required
                  ref={inputRef}
                  disabled={loading}
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                />
                {inputValue.trim() && (
                  <SendButton type="button" onClick={handleSendClick} disabled={loading}>
                    <Icon.ArrowUp />
                  </SendButton>
                )}
              </InputWrapper>

              <SuggestionsContainer>
                {suggestions.map((suggestion, index) => (
                  <SuggestionButton
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion.prompt, suggestion.action)}
                  >
                    {suggestion.action}
                  </SuggestionButton>
                ))}
              </SuggestionsContainer>
            </ChatBotForm>
          </WelcomeContent>
        </WelcomeContainer>
      ) : (
        <>
          <ChatHeader>
            <Icon.AiCoach />
            <ChatTitle>{i18n.t('builder.conversational_builder.chat_title')}</ChatTitle>
          </ChatHeader>

          <ChatBotForm onSubmit={handleSubmit}>
            <ChatBotBody ref={chatBodyRef} onScroll={trackScroll}>
              <DateSeparator>{i18n.t('builder.conversational_builder.date_today')}</DateSeparator>
              <MessagesWrapper>
                {messages.map((message, index) => (
                  <MessageContainer key={index} isUser={message.role === 'user'}>
                    <MessageBubble isUser={message.role === 'user'}>
                      {message.role === 'assistant' ? (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <Markdown>{message.content}</Markdown>
                          {/* <Copy onClick={() => navigator.clipboard.writeText(message.content)}>
                            Copy
                            <Icon.Copy />
                          </Copy> */}
                        </div>
                      ) : (
                        message.content
                      )}
                    </MessageBubble>
                    {message.role === 'assistant' && (
                      <Reactions>
                        {index === messages.length - 1 &&
                          !(applyState === 'applied' && (message.changes?.length ?? 0) > 0) && (
                            <ReactionButton
                              onClick={() => {
                                trackInternalEvent('chat_retry', { label: 'builder' })
                                handleRefreshWithCleanup()
                              }}
                            >
                              <Icon.Refresh />
                            </ReactionButton>
                          )}
                        {messageFeedback[index] === 'like' ? (
                          <ReactionButton $selected={true} style={{ cursor: 'default' }}>
                            <Icon.ThumbsUpFilled />
                          </ReactionButton>
                        ) : messageFeedback[index] === 'dislike' ? (
                          <ReactionButton $selected={true} style={{ cursor: 'default' }}>
                            <Icon.ThumbsDownFilled />
                          </ReactionButton>
                        ) : (
                          <>
                            <ReactionButton onClick={() => handleFeedback(index, 'like')}>
                              <Icon.ThumbsUp />
                            </ReactionButton>
                            <ReactionButton onClick={() => handleFeedback(index, 'dislike')}>
                              <Icon.ThumbsDown />
                            </ReactionButton>
                          </>
                        )}
                        {index === messages.length - 1 &&
                          message.role === 'assistant' &&
                          (message.changes?.length ?? 0) > 0 &&
                          (pendingChanges.length > 0 || applyState !== 'idle') && (
                            <ApplySuggestionsContainer
                              onClick={applyState === 'idle' ? handleApplyWithState : undefined}
                              $state={applyState}
                              $disabled={applyState !== 'idle'}
                            >
                              <ApplySuggestions $state={applyState}>
                                {applyState === 'applying' &&
                                  i18n.t('builder.conversational_builder.applying')}
                                {applyState === 'applied' &&
                                  i18n.t('builder.conversational_builder.applied')}
                                {applyState === 'idle' &&
                                  i18n.t('builder.conversational_builder.apply_suggestions', {
                                    count: pendingChanges.length,
                                  })}
                              </ApplySuggestions>
                              {applyState === 'applying' && <Icon.Loading />}
                              {applyState === 'applied' && <Icon20.Check />}
                              {applyState === 'idle' && <Icon.ArrowRight />}
                            </ApplySuggestionsContainer>
                          )}
                      </Reactions>
                    )}
                  </MessageContainer>
                ))}

                {loading && (
                  <Loader>
                    <Avatar>
                      <Icon.Assistant width={24} height={24} />
                    </Avatar>
                    <LoadingDots />
                  </Loader>
                )}

                {error && (
                  <ErrorMessage>
                    <Icon.CircleError /> {i18n.t(error)}
                  </ErrorMessage>
                )}
              </MessagesWrapper>

              {showScrollIndicator && (
                <ScrollIndicator type="button" onClick={scrollToBottom}>
                  <IconWrapper>
                    <Icon.ArrowDown />
                  </IconWrapper>
                </ScrollIndicator>
              )}
            </ChatBotBody>

            <InputContainer>
              <InputWrapper>
                <AutoExpandingPromptInput
                  name="prompt"
                  autoComplete="off"
                  autoFocus={true}
                  placeholder={i18n.t('builder.conversational_builder.ask_anything_placeholder')}
                  required
                  ref={inputRef}
                  disabled={loading}
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                />
                {inputValue.trim() && (
                  <SendButton type="button" onClick={handleSendClick} disabled={loading}>
                    <Icon.ArrowUp />
                  </SendButton>
                )}
              </InputWrapper>
            </InputContainer>
          </ChatBotForm>
        </>
      )}
    </ChatBotContainer>
  )
}

export default ConversationalBuilder
